import React, { useEffect, useState } from 'react';

import { ArrowRight, Compass, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Badge from '../../components/common/Badge';
import Card from '../../components/common/Card';
import AdminLayout from '../../components/layout/AdminLayout';
import MemberCoursesMap from '../../components/maps/MemberCoursesMap';
import MemberStats from '../../components/member/MemberStats';
import CourseCell from '../../components/tables/CourseCell';
import { MemberTypeConstants } from '../../constants/MemberTypeConstants';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useMyBucketList } from '../../hooks/useMemberBucketListApi';
import { useMyCoursesPlayed } from '../../hooks/useMemberCoursesPlayedApi';
import { useMyHomeCourses } from '../../hooks/useMemberHomeCourseApi';
import { MembershipStatusDTO } from '../../services/apiClient';

const PREVIEW_COUNT = 3;

const MemberDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: homeCourses, isLoading: isLoadingHomeCourses } =
    useMyHomeCourses();
  const { data: coursesPlayed, isLoading: isLoadingCoursesPlayed } =
    useMyCoursesPlayed();
  const { data: bucketList, isLoading: isLoadingBucketList } =
    useMyBucketList();
  const { createBillingPortalSession, getMyMembershipStatus } = useMemberApi();
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [membershipStatus, setMembershipStatus] =
    useState<MembershipStatusDTO | null>(null);
  // Manage Membership must not flash into view before this resolves: showing it, then hiding it
  // once we learn the member is Lifetime, is what let a stray click through and left the "could
  // not open the billing portal" error sitting on screen for a Lifetime member.
  const [isLoadingMembershipStatus, setIsLoadingMembershipStatus] =
    useState(true);

  useEffect((): (() => void) => {
    let cancelled = false;

    getMyMembershipStatus()
      .then((response) => {
        if (!cancelled && response?.success && response.data) {
          setMembershipStatus(response.data);
        }
      })
      .catch(() => {
        // A missing/failed status just means the badge and the Lifetime check below don't show -
        // nothing on this page depends on it hard enough to warrant its own error state.
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingMembershipStatus(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Case-insensitive, per CLAUDE.md: GUID casing is not guaranteed to survive a round trip, and a
  // miss here would offer a Lifetime member (every admin is one) a billing portal they have no use for.
  const isLifetimeMember =
    membershipStatus?.memberTypeId?.toLowerCase() ===
    MemberTypeConstants.LIFETIME_ID.toLowerCase();
  const canManageMembership = !isLoadingMembershipStatus && !isLifetimeMember;

  // If a click slipped in before the status above resolved (the race this page used to have),
  // don't leave that error sitting there once we learn they're Lifetime and never should have
  // been able to click it at all.
  useEffect(() => {
    if (isLifetimeMember && portalError) {
      setPortalError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLifetimeMember]);

  const handleManageMembership = async (): Promise<void> => {
    setPortalError(null);
    setIsOpeningPortal(true);
    try {
      const response = await createBillingPortalSession();
      if (response?.success && response.data) {
        window.location.href = response.data;
        return;
      }
      setPortalError(response?.message ?? 'Could not open the billing portal.');
    } catch (err: unknown) {
      const apiMessage = (err as { result?: { message?: string } })?.result
        ?.message;
      setPortalError(
        typeof apiMessage === 'string' && apiMessage.trim().length > 0
          ? apiMessage
          : 'Could not open the billing portal.',
      );
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const breadcrumbs = [{ label: 'Dashboard', href: '#' }];

  return (
    <AdminLayout
      activeMenuItem="/dashboard"
      breadcrumbs={breadcrumbs}
      allowedRoles={['User']}
      // Admins only: a plain member has no dashboard, their home is the profile.
      requiredPermission="Main.Dashboard.View"
    >
      <div className="p-6">
        {portalError && (
          <p
            role="alert"
            className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {portalError}
          </p>
        )}
        <Card
          title="My Courses"
          subtitle="Home courses, courses you've played, and your bucket list"
          headerActions={
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
              {membershipStatus?.memberTypeName && (
                <Badge
                  label={`${membershipStatus.memberTypeName} member`}
                  variant="brand"
                />
              )}
              {canManageMembership && (
                <button
                  type="button"
                  onClick={handleManageMembership}
                  disabled={isOpeningPortal}
                  className="flex items-center gap-1 text-sm font-medium text-primary-deep hover:underline disabled:cursor-wait disabled:opacity-60"
                >
                  <CreditCard className="h-4 w-4" />
                  {isOpeningPortal ? 'Opening...' : 'Manage Membership'}
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/explore')}
                className="flex items-center gap-1 text-sm font-medium text-primary-deep hover:underline"
              >
                <Compass className="h-4 w-4" /> Explore all courses
              </button>
            </div>
          }
        >
          <MemberStats />
          <MemberCoursesMap
            homeCourses={homeCourses || []}
            coursesPlayed={coursesPlayed || []}
            bucketList={bucketList || []}
          />
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 p-6 pt-0 lg:grid-cols-3">
        <Card
          title="Home Courses"
          subtitle={`${homeCourses?.length ?? 0} course${homeCourses?.length === 1 ? '' : 's'}`}
          headerActions={
            <button
              type="button"
              onClick={() => navigate('/member/home-courses')}
              className="flex items-center gap-1 text-sm font-medium text-primary-deep hover:underline"
            >
              View all <ArrowRight className="h-4 w-4" />
            </button>
          }
        >
          {isLoadingHomeCourses ? (
            <p className="text-sm text-ink dark:text-ink-muted">Loading...</p>
          ) : homeCourses && homeCourses.length > 0 ? (
            <div className="space-y-3">
              {homeCourses.slice(0, PREVIEW_COUNT).map((course) => (
                <CourseCell
                  key={course.id}
                  logoFileId={course.courseLogoFileId}
                  closed={course.courseClosed}
                  closedYear={course.courseClosedYear}
                  closedMonth={course.courseClosedMonth}
                  name={course.courseName || 'Unknown course'}
                  address={course.courseAddress}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink dark:text-ink-muted">
              You haven&apos;t set a home course yet.
            </p>
          )}
        </Card>

        <Card
          title="Courses Played"
          subtitle={`${coursesPlayed?.length ?? 0} course${coursesPlayed?.length === 1 ? '' : 's'}`}
          headerActions={
            <button
              type="button"
              onClick={() => navigate('/member/courses-played')}
              className="flex items-center gap-1 text-sm font-medium text-primary-deep hover:underline"
            >
              View all <ArrowRight className="h-4 w-4" />
            </button>
          }
        >
          {isLoadingCoursesPlayed ? (
            <p className="text-sm text-ink dark:text-ink-muted">Loading...</p>
          ) : coursesPlayed && coursesPlayed.length > 0 ? (
            <div className="space-y-3">
              {coursesPlayed.slice(0, PREVIEW_COUNT).map((course) => (
                <CourseCell
                  key={course.id}
                  logoFileId={course.courseLogoFileId}
                  closed={course.courseClosed}
                  closedYear={course.courseClosedYear}
                  closedMonth={course.courseClosedMonth}
                  name={course.courseName || 'Unknown course'}
                  address={course.courseAddress}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink dark:text-ink-muted">
              You haven&apos;t logged any played courses yet.
            </p>
          )}
        </Card>

        <Card
          title="Bucket List"
          subtitle={`${bucketList?.length ?? 0} course${bucketList?.length === 1 ? '' : 's'}`}
          headerActions={
            <button
              type="button"
              onClick={() => navigate('/member/bucket-list')}
              className="flex items-center gap-1 text-sm font-medium text-primary-deep hover:underline"
            >
              View all <ArrowRight className="h-4 w-4" />
            </button>
          }
        >
          {isLoadingBucketList ? (
            <p className="text-sm text-ink dark:text-ink-muted">Loading...</p>
          ) : bucketList && bucketList.length > 0 ? (
            <div className="space-y-3">
              {bucketList.slice(0, PREVIEW_COUNT).map((course) => (
                <CourseCell
                  key={course.id}
                  logoFileId={course.courseLogoFileId}
                  closed={course.courseClosed}
                  closedYear={course.courseClosedYear}
                  closedMonth={course.courseClosedMonth}
                  name={course.courseName || 'Unknown course'}
                  address={course.courseAddress}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink dark:text-ink-muted">
              Your bucket list is empty.
            </p>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default MemberDashboard;
